import { ContentPage, ContentPagePaged } from './content-page.model';
import { ContentSection, ContentSectionPaged } from './content-section.model';
import { ContentSectionGroup, ContentSectionGroupPaged } from './content-section-group.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, tap } from 'rxjs/operators';

import { FileConvertService } from '../../files-helpers/file-convert.service';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PagerQueryModel } from './pager-query.model';
import { SearchType } from '@nrwl/rx-models';
import { environment } from '@nrwl/environment';

@Injectable({
  providedIn: 'root',
})
export class ContentEditorService {
  dataURLRegex =
    /^\s*data:([a-z]+\/[a-z]+(;[a-z\-]+\=[a-z\-]+)?)?(;base64)?,[a-z0-9\!\$\&\'\,\(\)\*\+\,\;\=\-\.\_\~\:\@\/\?\%\s]*\s*$/i;

  constructor(
    private http: HttpClient,
    private fileConvertService: FileConvertService,
  ) {}

  // TODO: Define Stencil model, refactor and extend FormConfig (form-config.model.ts)
  public getFieldStencils(includeSystem: boolean = false): Observable<any> {
    return this.http.get<any>('assets/data/content-editor/content-editor-stencil.data.json').pipe(
      map((res: any[]) => {
        if (includeSystem) {
          return res.map((stencils) => stencils);
        }
        return res.filter((stencil) => !stencil.system);
      }),
    );
  }

  public getBAStencils(includeSystem: boolean = false): Observable<any> {
    return this.http
      .get<any>('assets/data/content-editor/content-editor-ba-stencils.data.json')
      .pipe(
        map((res: any[]) => {
          if (includeSystem) {
            return res.map((stencils) => stencils);
          }
          return res.filter((stencil) => !stencil.system);
        }),
      );
  }

  /******************************************
   *
   * Content Section Groups aka Categories (1rd level)
   *
   */

  // Get Categories
  public getContentSectionGroups(search: string = ''): Observable<ContentSectionGroupPaged> {
    const params = new HttpParams()
      .append('limit', '1000')
      .append('ordering', 'order')
      .append('search', search);
    return this.http
      .get<ContentSectionGroupPaged>(environment.baseAPIUrlv3 + 'content-section-group/', {
        params,
      })
      .pipe(
        map((res: ContentSectionGroupPaged) => {
          return new ContentSectionGroupPaged(res);
        }),
      );
  }

  // Get Category By ID
  public getContentSectionGroupById(id: number): Observable<ContentSectionGroup> {
    return this.http.get<any>(environment.baseAPIUrlv3 + 'content-section-group/' + id + '/').pipe(
      map((res: ContentSectionGroup) => {
        return new ContentSectionGroup(res);
      }),
    );
  }

  public createCategory(category: ContentSectionGroup) {
    return this.http.post<any>(environment.baseAPIUrlv3 + 'content-section-group/', category).pipe(
      map((res: ContentSectionGroup) => {
        console.log('GET /v3/content-section-group/', res);
        return res;
      }),
    );
  }

  /************************************
   *
   * Content Sections aka Category Items (2rd level)
   *
   */

  // Get All Category Items
  public getContentSections(): Observable<ContentSectionPaged> {
    return this.http.get<any>(environment.baseAPIUrlv3 + 'content-section/').pipe(
      map((res: ContentSectionPaged) => {
        console.log('GET /v3/content-section/', res);
        return new ContentSectionPaged(res);
      }),
    );
  }

  // Get Category Item by Id
  public getContentSectionById(
    sectionId: number,
    searchType: SearchType = SearchType.GlobalLibrary,
  ): Observable<ContentSection> {
    return this.http.get<any>(environment.baseAPIUrlv3 + 'content-section/' + sectionId + '/').pipe(
      map((res: ContentSection) => {
        // console.log('GET /v3/content-section/' + sectionId + '/', res)
        return new ContentSection(res);
      }),
    );
  }

  // Get Category Items by Category ID (Account Only)
  public getContentSectionsByGroupId(
    query: PagerQueryModel | any,
  ): Observable<ContentSection[] | any> {
    const params: any = [];
    params.push('limit=1000');
    params.push('content_section_id=' + query.categoryID);
    return (
      this.http
        // DISABLED DUE TO: RWE-1639
        //.get<any>(environment.baseAPIUrlv3 + 'content-section-group/' + query.categoryID + '/sections/?is_stock=true')
        .get<any>(environment.baseAPIUrlv3 + 'content-section/?' + params.join('&'))
        .pipe(
          map((res: ContentSectionPaged) => {
            // console.log('GET /v3/content-section-group/' + query.categoryID + '/sections/', res)
            return res.results.map((item) => {
              return new ContentSection(item);
            });
          }),
        )
    );
  }

  // Get content sections by search key
  public getFilteredContentSections(key: string): Observable<ContentSection[]> {
    let params = new HttpParams();
    params = params.append('limit', '1000');
    params = params.append('ordering', 'order');
    if (key) {
      params = params.append('search', key);
    }

    return this.http.get<any>(environment.baseAPIUrlv3 + 'content-section/', { params }).pipe(
      map((res: ContentSectionPaged) => {
        return res.results.map((item) => {
          return new ContentSection(item);
        });
      }),
    );
  }

  // Get Category Items by Category ID (Account Only)
  public getContentSectionPages(sectionId: number) {
    return this.http
      .get<any>(environment.baseAPIUrlv3 + 'content-section/' + sectionId + '/pages/?limit=100')
      .pipe(
        map((res: ContentPagePaged) => {
          // console.log('GET /v3/getContentSectionPages/' + sectionId + '/pages/', res)
          return new ContentPagePaged(res);
        }),
      );
  }

  // Get Admin Category Items by Category ID (Admin endpoint)
  public getAdminContentSectionsByGroupId(id: number) {
    return this.http
      .get<any>(
        environment.baseAPIUrlv3 +
          'content-section/?section_group_id=' +
          id +
          '&is_stock=false&archived=false',
      )
      .pipe(
        map((res: ContentSectionGroupPaged[]) => {
          return res;
        }),
      );
  }

  /************************************
   *
   * Content Pages aka Category Sub-Items (3rd level)
   *
   */

  public getContentPages(): Observable<ContentPagePaged> {
    return this.http.get<any>(environment.baseAPIUrlv3 + 'content-page/').pipe(
      map((res: ContentPagePaged) => {
        return res;
      }),
    );
  }

  public getContentPageById(id: number): Observable<ContentPage> {
    return this.http.get<any>(environment.baseAPIUrlv3 + 'content-page/' + id + '/').pipe(
      map((res: ContentPage) => {
        return res;
      }),
    );
  }

  public getContentPagesBySectionId(
    sectionId: number,
    searchType: SearchType = SearchType.GlobalLibrary,
  ): Observable<ContentPagePaged> {
    let params = new HttpParams().append('ordering', 'order');
    params = params.append('limit', '100');
    if (searchType === SearchType.GlobalLibrary || searchType === SearchType.RxLibrary) {
      params = params.append('is_stock', 'true');
    }
    return this.http
      .get<any>(environment.baseAPIUrlv3 + 'content-section/' + sectionId + '/pages/', { params })
      .pipe(
        map((res: ContentPagePaged) => {
          return new ContentPagePaged(res);
        }),
      );
  }

  public createContentSectionGroup(item: any) {
    const formData = new FormData();
    formData.append('name', item.name);
    formData.append('banner', this.getBanner(item.banner));
    formData.append('shared_on_app', item.shared_on_app.toString());
    formData.append('shared_on_web', item.shared_on_web.toString());

    return this.http
      .post(environment.baseAPIUrlv3 + `content-section-group/`, formData)
      .pipe(map((tmpl) => new ContentSectionGroup(tmpl)));
  }

  public createContentSection(item: ContentSection) {
    const formData = new FormData();

    formData.append('name', item.name);

    if (item.section_group) {
      formData.append('section_group', item.section_group.toString());
    }

    formData.append('banner', this.getBanner(item.banner));

    if (item.is_published === undefined) {
      formData.append('is_published', 'false');
    } else {
      formData.append('is_published', item.is_published.toString());
    }

    return this.http
      .post(environment.baseAPIUrlv3 + `content-section/`, formData)
      .pipe(map((created) => new ContentSection(created)));
  }

  public cloneCategortItems(item: ContentSection) {
    const formData = new FormData();

    if (item.section_group) {
      formData.append('target_group_id', item.section_group.toString());
    }

    return this.http
      .post(environment.baseAPIUrlv3 + `content-section/${item.id}/clone/`, formData)
      .pipe(map((created) => new ContentSection(created)));
  }

  public createContentPage(item: ContentPage): Observable<ContentPage> {
    const data = { ...item };
    data.content = JSON.parse(data.content);
    console.log(data);
    return this.http
      .post(environment.baseAPIUrlv3 + `content-page/`, data)
      .pipe(map((tmpl) => new ContentPage(tmpl)));
  }

  public updateContentSectionGroup(
    item: ContentSectionGroup | any = null,
    fields: string[] | any = null,
  ): Observable<ContentSectionGroup> {
    const formData = new FormData();
    if (fields) {
      fields.map((field) => {
        formData.append(field, item[field]);
      });
    } else {
      formData.append('name', item.name);
      formData.append('shared_on_app', item.shared_on_app.toString());
      formData.append('shared_on_web', item.shared_on_web.toString());
      formData.append('banner', this.getBanner(item.banner));

      if (item.position_after) {
        formData.append('position_after', item.position_after.toString());
      }
    }

    return this.http
      .patch(environment.baseAPIUrlv3 + `content-section-group/${item.id}/`, formData)
      .pipe(map((tmpl) => new ContentSectionGroup(tmpl)));
  }

  public updateContentSection(
    item: ContentSection,
    fields: string[] | any = null,
  ): Observable<ContentSection> {
    const formData = new FormData();

    if (fields) {
      fields.map((field) => {
        formData.append(field, item[field]);
      });
    } else {
      if (item.name) {
        formData.append('name', item.name);
      }
      if (item.is_published !== undefined) {
        formData.append('is_published', `${item.is_published}`);
      }
      if (item.section_group) {
        formData.append('section_group', `${item.section_group}`);
      }
      if (item.is_deleted !== undefined) {
        formData.append('is_deleted', `${item.is_deleted}`);
      }

      const tempBanner = this.getBanner(item.banner);
      if (tempBanner) {
        formData.append('banner', tempBanner);
      }

      if (item.position_after) {
        formData.append('position_after', `${item.position_after}`);
      }
      if (item.order === 0) {
        formData.append('order', `0`);
      }
    }

    return this.http
      .patch(environment.baseAPIUrlv3 + `content-section/${item.id}/`, formData)
      .pipe(map((tmpl) => new ContentSection(tmpl)));
  }

  public updateContentPage(item: any): Observable<ContentPage> {
    const data = { ...item };
    return this.http
      .patch(environment.baseAPIUrlv3 + `content-page/${item.id}/`, data)
      .pipe(map((tmpl) => new ContentPage(tmpl)));
  }

  public sortContentPage(item: any): Observable<ContentPage> {
    const formData = new FormData();
    if (item.position_after) {
      formData.append('position_after', item.position_after.toString());
    } else {
      formData.append('order', item.order.toString());
    }

    return this.http
      .patch(environment.baseAPIUrlv3 + `content-page/${item.id}/`, formData)
      .pipe(map((tmpl) => new ContentPage(tmpl)));
  }

  public archiveCategory(id: number): Observable<any> {
    return this.http.delete(environment.baseAPIUrlv3 + `content-section-group/${id}/`);
  }

  public archiveCategoryItem(id: number): Observable<any> {
    return this.http.delete(environment.baseAPIUrlv3 + `content-section/${id}/`);
  }

  public archiveSectionPage(id: number): Observable<any> {
    return this.http.delete(environment.baseAPIUrlv3 + `content-page/${id}/`);
  }

  public restoreCategory(item: any): Observable<ContentSectionGroup> {
    const data = new ContentSectionGroup({
      id: item.id,
      name: item.name,
      order: 0,
      is_deleted: false,
    });
    return this.updateContentSectionGroup(data);
  }

  public restoreCategoryItem(item: any) {
    const data = new ContentSection({
      id: item.id,
      name: item.name,
      section_group: item.section_group,
      order: 0,
      is_deleted: false,
    });

    return this.updateContentSection(data);
  }

  private isDataURL(s) {
    return !!s.match(this.dataURLRegex);
  }

  private getBanner(banner) {
    let result: any = '';
    if (banner) {
      if ((banner as any) instanceof File) {
        result = banner;
      } else if (this.isDataURL(banner)) {
        result = this.fileConvertService.b64toFile(banner, 'banner');
      }
    }
    return result;
  }
}
